import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/lib/supabase';
import { UserProfile, ProjectAccess, UserRole } from '@/types/user';

export interface PermissionContext {
  // User profile data
  userProfile: UserProfile | null;
  projectAccess: ProjectAccess[];
  loading: boolean;
  error: string | null;

  // Role checks
  isSuperAdmin: boolean;
  isOwner: boolean;
  isPipelineManager: boolean;
  isTeamLead: boolean;
  isTeamMember: boolean;
  isExternalPartner: boolean;
  isPending: boolean;
  isIntern: boolean;

  // Global permissions (computed from role)
  canManageUsers: boolean;
  canMarkAsPaid: boolean;
  canDeleteProjects: boolean;
  canViewAllProjects: boolean;
  canViewAuditLogs: boolean;
  canViewCRM: boolean;
  canViewProjects: boolean;
  canEditProjects: boolean;
  canManageInterns: boolean;
  canManagePipeline: boolean;

  // Project-specific permission checks
  canViewProject: (projectId: string) => boolean;
  canEditProject: (projectId: string) => boolean;
  canViewProjectFinancials: (projectId: string) => boolean;
  canViewProjectTeam: (projectId: string) => boolean;
  canViewProjectPayments: (projectId: string) => boolean;

  // Actions
  refreshProfile: () => Promise<void>;
  updateLastLogin: () => Promise<void>;
}

export function usePermissions(): PermissionContext {
  const { user } = useAuth();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [projectAccess, setProjectAccess] = useState<ProjectAccess[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch user profile from user_profiles table
  const fetchUserProfile = useCallback(async () => {
    if (!user?.id) {
      setUserProfile(null);
      setProjectAccess([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch user profile by auth_user_id first
      const result = await supabase
        .from('user_profiles')
        .select('*')
        .eq('auth_user_id', user.id)
        .single();
      const { error: profileError } = result;
      let profile = result.data;

      if (profileError) {
        // Profile might not exist yet if this is a brand new signup
        // The database trigger should create it, but there might be a race condition
        if (profileError.code === 'PGRST116') {
          // No rows returned by auth_user_id - try email lookup for seeded users
          if (user.email) {
            const { data: profileByEmail, error: emailError } = await supabase
              .from('user_profiles')
              .select('*')
              .eq('email', user.email)
              .single();

            if (!emailError && profileByEmail) {
              // Found profile by email - link it to auth_user_id for future lookups
              await supabase.rpc('link_auth_user_to_profile');
              profile = profileByEmail;
            } else {
              console.warn('User profile not found, may be creating...');
              setUserProfile(null);
            }
          } else {
            console.warn('User profile not found, may be creating...');
            setUserProfile(null);
          }
        } else {
          throw profileError;
        }
      }

      if (profile) {
        setUserProfile(profile);
      }

      // Project-level access not yet implemented (table doesn't exist)
      // Super admins and owners bypass project access checks anyway
      setProjectAccess([]);
    } catch (err) {
      console.error('Error fetching user profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to load user profile');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Update last login timestamp
  const updateLastLogin = useCallback(async () => {
    if (!user?.id) return;

    try {
      const { error } = await supabase.rpc('update_last_login');
      if (error) {
        console.error('Error updating last login:', error);
      }
    } catch (err) {
      console.error('Error updating last login:', err);
    }
  }, [user?.id]);

  // Refresh profile data
  const refreshProfile = useCallback(async () => {
    await fetchUserProfile();
  }, [fetchUserProfile]);

  // Fetch profile when user changes
  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  // Update last login when user logs in
  useEffect(() => {
    if (user?.id && userProfile) {
      updateLastLogin();
    }
  }, [user?.id, userProfile, updateLastLogin]);

  // Memoized role checks
  const role = userProfile?.role;

  const isSuperAdmin = role === 'super_admin';
  const isOwner = role === 'owner';
  const isPipelineManager = role === 'pipeline_manager';
  const isTeamLead = role === 'team_lead';
  const isTeamMember = role === 'team_member';
  const isExternalPartner = role === 'external_partner';
  const isPending = role === 'pending';
  const isIntern = role === 'intern';

  // Global permissions based on role.
  // Interns are scoped to the /intern section only — they get none of the
  // portfolio / projects / CRM surfaces.
  // Pipeline managers are scoped to the two prospect pipelines: they get CRM
  // read (the GET /crm endpoints are unauthenticated beyond login; every write
  // is requireAdminOrOwner) but no projects surface, because that is where the
  // financials live.
  // Team leads are team members who also own the pipelines: they keep projects,
  // their per-project access grants and their personal dashboard, and gain
  // /prospects and /journey on top. Still no financials or user management.
  const permissions = useMemo(() => ({
    canManageUsers: isSuperAdmin,
    canMarkAsPaid: isSuperAdmin,
    canDeleteProjects: isSuperAdmin,
    canViewAllProjects: isSuperAdmin || isOwner,
    canViewAuditLogs: isSuperAdmin || isOwner,
    canViewCRM: !isPending && !isExternalPartner && !isIntern,
    canViewProjects: !isPending && !isIntern && !isPipelineManager,
    canEditProjects: isSuperAdmin || isOwner,
    // Intern admin console — super_admin only (owners/members excluded by design)
    canManageInterns: isSuperAdmin,
    // Both prospect pipelines: /prospects and /journey. Mirrors the SQL
    // predicate in public.is_pipeline_worker().
    canManagePipeline: isSuperAdmin || isOwner || isPipelineManager || isTeamLead,
  }), [isSuperAdmin, isOwner, isPending, isExternalPartner, isIntern, isPipelineManager, isTeamLead]);

  // Project-specific permission checks
  const canViewProject = useCallback((projectId: string): boolean => {
    if (isPending) return false;
    if (isSuperAdmin || isOwner) return true;
    return projectAccess.some(a => a.project_id === projectId);
  }, [isPending, isSuperAdmin, isOwner, projectAccess]);

  const canEditProject = useCallback((projectId: string): boolean => {
    if (isPending) return false;
    if (isSuperAdmin || isOwner) return true;
    const access = projectAccess.find(a => a.project_id === projectId);
    return access?.permission === 'edit' || access?.permission === 'admin';
  }, [isPending, isSuperAdmin, isOwner, projectAccess]);

  const canViewProjectFinancials = useCallback((projectId: string): boolean => {
    if (isPending) return false;
    if (isSuperAdmin || isOwner) return true;
    const access = projectAccess.find(a => a.project_id === projectId);
    return access?.can_view_financials ?? false;
  }, [isPending, isSuperAdmin, isOwner, projectAccess]);

  const canViewProjectTeam = useCallback((projectId: string): boolean => {
    if (isPending) return false;
    if (isSuperAdmin || isOwner) return true;
    const access = projectAccess.find(a => a.project_id === projectId);
    return access?.can_view_team ?? false;
  }, [isPending, isSuperAdmin, isOwner, projectAccess]);

  const canViewProjectPayments = useCallback((projectId: string): boolean => {
    if (isPending) return false;
    if (isSuperAdmin || isOwner) return true;
    const access = projectAccess.find(a => a.project_id === projectId);
    return access?.can_view_payments ?? false;
  }, [isPending, isSuperAdmin, isOwner, projectAccess]);

  return {
    userProfile,
    projectAccess,
    loading,
    error,
    isSuperAdmin,
    isOwner,
    isPipelineManager,
    isTeamLead,
    isTeamMember,
    isExternalPartner,
    isPending,
    isIntern,
    ...permissions,
    canViewProject,
    canEditProject,
    canViewProjectFinancials,
    canViewProjectTeam,
    canViewProjectPayments,
    refreshProfile,
    updateLastLogin,
  };
}
